import {
  Bot,
  CalendarClock,
  CreditCard,
  Loader2,
  Mail,
  Phone,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useGetList, useNotify, useRefresh } from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { SectionCard } from "../data-display/SectionCard";
import { ListPageHeader } from "../list/ListPageHeader";
import { unwrapFunctionMessage } from "../lib/functionErrors";
import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

interface Connection {
  id: string;
  provider: string;
  status: string;
  config: Record<string, unknown>;
}

interface PhoneNumberRow {
  id: string;
  e164: string;
  kind: string;
  status: string;
}

const StatusDot = ({ state }: { state: "on" | "off" | "warn" }) => (
  <span
    className={cn(
      "inline-block size-2.5 shrink-0 rounded-full",
      state === "on" && "bg-emerald-500",
      state === "warn" && "bg-amber-500",
      state === "off" && "bg-muted-foreground/30",
    )}
  />
);

/**
 * Settings, Connections: every service THIS workspace uses, in plain cards.
 *
 * Two kinds of money stay visibly separate: Plan & Billing (in Settings)
 * is what the business pays GRAVUN; the Payments card here is where the
 * business gets paid BY ITS CUSTOMERS through its own Stripe account.
 * Secrets never appear on this page; connect actions open provider-hosted
 * flows, and status comes from integration_connections rows the server keeps.
 */
export const ConnectionsPage = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const workspaceId = getActiveWorkspaceId();
  const [busy, setBusy] = useState<string | null>(null);

  const { data: connections } = useGetList<Connection>(
    "integration_connections",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "created_at", order: "DESC" },
    },
  );
  const { data: numbers } = useGetList<PhoneNumberRow>("phone_numbers", {
    pagination: { page: 1, perPage: 20 },
    sort: { field: "created_at", order: "DESC" },
  });

  const byProvider = (provider: string) =>
    (connections ?? []).find((c) => c.provider === provider);

  const invoke = async (
    fn: string,
    body: Record<string, unknown>,
    key: string,
  ) => {
    setBusy(key);
    try {
      const { data, error } = await getSupabaseClient().functions.invoke(fn, {
        body: { ...body, workspace_id: workspaceId },
      });
      if (error) {
        const context = (error as { context?: Response }).context;
        let message = error.message;
        if (context && typeof context.json === "function") {
          try {
            message = unwrapFunctionMessage(await context.json()) ?? message;
          } catch {
            // keep the generic message
          }
        }
        throw new Error(message);
      }
      const payload = data as { url?: string };
      if (payload?.url) {
        window.location.href = payload.url;
        return;
      }
      refresh();
    } catch (caught) {
      notify(
        caught instanceof Error ? caught.message : "The connection failed",
        { type: "warning" },
      );
    } finally {
      setBusy(null);
    }
  };

  // Returning from a provider consent screen: the callback appended
  // ?oauth=connected|denied|failed to the hash URL.
  useEffect(() => {
    const hash = window.location.hash;
    const query = hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : "";
    const params = new URLSearchParams(query);
    const outcome = params.get("oauth");
    if (!outcome) return;
    const provider = params.get("provider") ?? "the service";
    notify(
      outcome === "connected"
        ? `Connected ${provider}. You are ready to go.`
        : outcome === "denied"
          ? "Sign-in was cancelled; nothing was connected."
          : "The provider sign-in failed; nothing was connected. Try again.",
      { type: outcome === "connected" ? "success" : "info" },
    );
    window.location.hash = "#/connections";
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const startOauth = (provider: string) =>
    void invoke(
      "oauth-service",
      {
        action: "start",
        provider,
        return_url: `${window.location.origin}/#/connections`,
      },
      provider,
    );

  const disconnectOauth = (provider: string) =>
    void invoke("oauth-service", { action: "disconnect", provider }, provider);

  const oauthAction = (provider: string, connection?: Connection) =>
    connection?.status === "connected" ? (
      <span className="flex items-center gap-1.5">
        <Button
          size="sm"
          variant="ghost"
          disabled={busy === provider}
          onClick={() => disconnectOauth(provider)}
        >
          Disconnect
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy === provider}
          onClick={() => startOauth(provider)}
        >
          Reconnect
        </Button>
      </span>
    ) : (
      <Button
        size="sm"
        variant="outline"
        disabled={busy === provider}
        onClick={() => startOauth(provider)}
      >
        {busy === provider ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : connection?.status === "error" ? (
          "Reconnect"
        ) : (
          "Connect"
        )}
      </Button>
    );

  const stripeConnect = byProvider("stripe_connect");
  const gmail = byProvider("gmail");
  const outlook = byProvider("outlook");
  const vapi = byProvider("vapi");
  const googleCal = byProvider("google_calendar");
  const microsoftCal = byProvider("microsoft_calendar");
  const activeNumbers = (numbers ?? []).filter((n) => n.status === "active");

  const stateOf = (connection?: Connection): "on" | "off" | "warn" =>
    connection?.status === "connected"
      ? "on"
      : connection?.status === "pending" || connection?.status === "error"
        ? "warn"
        : "off";

  const stateLabel = (connection?: Connection) =>
    connection?.status === "connected"
      ? "Connected"
      : connection?.status === "pending"
        ? "Needs attention: setup incomplete"
        : connection?.status === "error"
          ? "Needs attention"
          : "Not connected";

  return (
    <div className="flex flex-col gap-4">
      <ListPageHeader
        title="Connections"
        subtitle="The services this workspace uses. Your Gravun subscription lives separately under Plan & Billing."
      />

      {/* GET PAID */}
      <SectionCard title="Payments">
        <div className="flex flex-col gap-1 px-1">
          <Row
            icon={CreditCard}
            name="Stripe Payments"
            detail={
              stripeConnect?.status === "connected"
                ? "Accepting customer payments"
                : "Accept payments from your customers: deposits, invoices, payment links"
            }
            state={stateOf(stripeConnect)}
            stateLabel={stateLabel(stripeConnect)}
            action={
              <Button
                size="sm"
                variant={
                  stripeConnect?.status === "connected" ? "outline" : "default"
                }
                disabled={busy === "stripe_connect"}
                onClick={() =>
                  void invoke(
                    "stripe-billing",
                    {
                      action: "connect_onboard",
                      return_url: window.location.href,
                    },
                    "stripe_connect",
                  )
                }
              >
                {busy === "stripe_connect" ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : stripeConnect?.status === "connected" ? (
                  "Manage"
                ) : stripeConnect?.status === "pending" ? (
                  "Finish setup"
                ) : (
                  "Connect Stripe"
                )}
              </Button>
            }
          />
        </div>
      </SectionCard>

      {/* Email */}
      <SectionCard title="Email">
        <div className="flex flex-col gap-1 px-1">
          <Row
            icon={Mail}
            name="Gmail"
            detail={
              (gmail?.config?.email as string) ??
              "Send, receive and thread from your Google mailbox"
            }
            state={stateOf(gmail)}
            stateLabel={stateLabel(gmail)}
            action={oauthAction("gmail", gmail)}
          />
          <Row
            icon={Mail}
            name="Microsoft 365 / Outlook"
            detail={
              (outlook?.config?.email as string) ??
              "Send, receive and thread from your Microsoft mailbox"
            }
            state={stateOf(outlook)}
            stateLabel={stateLabel(outlook)}
            action={oauthAction("outlook", outlook)}
          />
        </div>
      </SectionCard>

      {/* Phone */}
      <SectionCard title="Phone & SMS">
        <div className="flex flex-col gap-1 px-1">
          <Row
            icon={Phone}
            name="Gravun Business Phone"
            detail={
              activeNumbers.length > 0
                ? activeNumbers.map((n) => n.e164).join(", ")
                : "A business number for calls and texts, right in your inbox. $19/month including your first local number."
            }
            state={activeNumbers.length > 0 ? "on" : "off"}
            stateLabel={
              activeNumbers.length > 0
                ? `${activeNumbers.length} number${activeNumbers.length === 1 ? "" : "s"}`
                : "Not set up"
            }
            action={
              <Button size="sm" variant="outline" asChild>
                <a href="#/phone">
                  {activeNumbers.length > 0 ? "Manage" : "Get a number"}
                </a>
              </Button>
            }
          />
        </div>
      </SectionCard>

      {/* AI + Calendar */}
      <SectionCard title="AI & Calendar">
        <div className="flex flex-col gap-1 px-1">
          <Row
            icon={Bot}
            name="Vapi AI Voice"
            detail="AI answers and makes calls; billed as usage because voice AI carries real cost"
            state={stateOf(vapi)}
            stateLabel={
              vapi?.status === "connected" ? "Enabled" : "Not enabled"
            }
            action={
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  notify(
                    "AI voice needs the phone service first, then the owner enables Vapi (PRODUCTION_INTEGRATIONS.md).",
                    { type: "info" },
                  )
                }
              >
                Learn more
              </Button>
            }
          />
          <Row
            icon={CalendarClock}
            name="Google Calendar"
            detail="Two-way sync for appointments and booking"
            state={stateOf(googleCal)}
            stateLabel={stateLabel(googleCal)}
            action={oauthAction("google_calendar", googleCal)}
          />
          <Row
            icon={CalendarClock}
            name="Microsoft Calendar"
            detail="Two-way sync for appointments and booking"
            state={stateOf(microsoftCal)}
            stateLabel={stateLabel(microsoftCal)}
            action={oauthAction("microsoft_calendar", microsoftCal)}
          />
        </div>
      </SectionCard>
    </div>
  );
};

const Row = ({
  icon: Icon,
  name,
  detail,
  state,
  stateLabel,
  action,
}: {
  icon: React.ComponentType<{ className?: string }>;
  name: string;
  detail: string;
  state: "on" | "off" | "warn";
  stateLabel: string;
  action: React.ReactNode;
}) => (
  <div className="flex items-center gap-3 border-b border-border-subtle py-3 last:border-0">
    <Icon className="size-4 shrink-0 text-muted-foreground" />
    <div className="min-w-0 flex-1">
      <p className="flex items-center gap-2 text-[13.5px] font-medium">
        {name}
        <StatusDot state={state} />
        <span className="text-[11.5px] font-normal text-muted-foreground">
          {stateLabel}
        </span>
      </p>
      <p className="truncate text-[12px] text-muted-foreground">{detail}</p>
    </div>
    {action}
  </div>
);
